// =============================================================================
// cart-mini.js — Symington Pharmacy
//
// Handles cart UI on every page:
//   - The floating mini-cart drawer (rendered by the `cart-mini` section).
//   - The /cart page (rendered by `template--<id>__cart-items` and
//     `template--<id>__cart-footer` template sections).
//
// FIX summary:
//   The "BOGO Blowout" cart-transform splits the variant into two line items
//   (paid + free) that share variant_id but have different line keys. Old
//   code mutated the DOM optimistically (matching by variant_id only),
//   which left the drawer out of sync until full page reload — wrong line
//   counts, $0 ghost lines after delete, etc.
//
//   New approach: after every /cart/add.js or /cart/update.js, ask Shopify's
//   Section Rendering API for fresh HTML of every cart-related section on
//   the current page and swap in the relevant blocks (line items, totals,
//   free-shipping notice). The server is the source of truth. Progress bar
//   width is JS-computed from the cart JSON returned in the same response.
// =============================================================================

const MINI_CART_SECTION_ID = 'cart-mini';

// Class selectors that live inside cart-related sections and need to be
// re-rendered from the server after every mutation. Each must be a
// self-contained block (don't include `.mini-cart__info` because it wraps
// `.mini-cart__orders` and the two would race).
const SWAP_SELECTORS = [
  '.upsell-container',     // "Add $X more to qualify for free shipping" + progress bar shell
  '.mini-cart__orders',    // line items list (mini cart + /cart page both use this)
  '.mini-cart__pricelist', // mini-cart subtotal / compare-at price
  '.mini-cart__heading',   // "Items in cart: (N)"
  '.totals',               // /cart page "Estimated total" block (CAD suffix etc.)
  '.totals__total-value',  // fallback when only the value lives outside .totals
  '.js-contents',          // Dawn-style cart-footer inner wrapper
];

// -----------------------------------------------------------------------------
// Drawer open/close + overlay
// -----------------------------------------------------------------------------
function showMiniCart() {
  document.querySelector('.mini-cart')?.classList.add('active');
}
function closeMiniCart() {
  document.querySelector('.mini-cart')?.classList.remove('active');
}
const closeMiniCartButton = document.querySelector('.mini-cart__close');
if (closeMiniCartButton) closeMiniCartButton.addEventListener('click', closeMiniCart);
if (typeof openCartBtn !== 'undefined' && openCartBtn) {
  openCartBtn.forEach((btn) => btn.addEventListener('click', showMiniCart));
}

function showCartOverlay() {
  document.querySelector('.overlay-cart')?.classList.add('active');
}
function hideCartOverlay() {
  document.querySelector('.overlay-cart')?.classList.remove('active');
}

function showConfirm(message) {
  const confirmBox = document.getElementById('customConfirm');
  const overlay = document.getElementById('overlay-fullscreen');
  const confirmMessage = document.getElementById('confirmMessage');
  confirmMessage.textContent = message;
  confirmBox.style.display = 'block';
  overlay.style.display = 'block';
  return new Promise((resolve) => {
    const finish = (val) => {
      confirmBox.style.display = 'none';
      overlay.style.display = 'none';
      resolve(val);
    };
    document.getElementById('confirmOk').onclick = () => finish(true);
    document.getElementById('confirmCancel').onclick = () => finish(false);
    document.getElementById('overlay-fullscreen').onclick = () => finish(false);
  });
}

// -----------------------------------------------------------------------------
// Auto-detect every Shopify section wrapper on the current page that hosts a
// cart UI element. Returns section IDs ready to pass to `?sections=…`.
//
// Why: the cart page's wrappers use template-scoped IDs like
// `template--21610883547359__cart-footer` that differ per store, while the
// floating drawer always uses `cart-mini`. This way we don't hardcode either.
// -----------------------------------------------------------------------------
function getCartSectionIdsOnPage() {
  const ids = new Set([MINI_CART_SECTION_ID]);
  const indicators = [
    '.mini-cart__orders',
    '.totals__total-value',
    '.upsell-container',
    '.js-contents',
  ];
  indicators.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      let p = el;
      while (p) {
        if (p.id && p.id.indexOf('shopify-section-') === 0) {
          ids.add(p.id.replace('shopify-section-', ''));
          break;
        }
        p = p.parentElement;
      }
    });
  });
  return Array.from(ids);
}

// -----------------------------------------------------------------------------
// Apply a sections map (from /cart/update.js or /cart/add.js with
// `?sections=...`) to the page. For each returned section, find its wrapper
// in the live DOM and swap in the known cart UI blocks from the fresh HTML.
// -----------------------------------------------------------------------------
function renderCartFromSections(sectionsMap, cartData) {
  if (sectionsMap && typeof sectionsMap === 'object') {
    Object.entries(sectionsMap).forEach(([sectionId, html]) => {
      if (!html || typeof html !== 'string') return;
      const wrapperId = `shopify-section-${sectionId}`;
      const wrapper = document.getElementById(wrapperId);
      if (!wrapper) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.getElementById(wrapperId) || doc.body;

      SWAP_SELECTORS.forEach((sel) => {
        const newEl = incoming.querySelector(sel);
        const oldEl = wrapper.querySelector(sel);
        if (newEl && oldEl) {
          oldEl.replaceWith(newEl.cloneNode(true));
        }
      });
    });
  }

  // The free-shipping progress bar width is set in JS, not Liquid, so we
  // recompute it from the fresh cart total.
  updateFreeShippingProgress(cartData);

  // Re-bind quantity/delete buttons against the newly inserted nodes.
  bindLineItemHandlers();
}

// Fallback when a caller doesn't have a sections map (e.g. Avada upsell flow).
async function refetchAndRenderCart() {
  const sections = getCartSectionIdsOnPage();
  const url = `/cart?sections=${encodeURIComponent(sections.join(','))}`;
  try {
    const res = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
    const map = await res.json();
    const cartData = await fetch('/cart.js').then((r) => r.json());
    renderCartFromSections(map, cartData);
  } catch (err) {
    console.error('refetchAndRenderCart failed:', err);
  }
}

// -----------------------------------------------------------------------------
// Free-shipping progress bar fill. Liquid renders the markup but not the
// width — JS computes it from cart.total_price vs the threshold stored on
// `.progress-bar-done[data-shiped_price]` (cents).
// -----------------------------------------------------------------------------
function updateFreeShippingProgress(cartData) {
  if (!cartData) return;
  document.querySelectorAll('.progress-bar-done').forEach((bar) => {
    const threshold = Number(bar.dataset.shiped_price) || 0;
    if (threshold <= 0) return;
    const percent = Math.min(100, (cartData.total_price / threshold) * 100);
    bar.style.width = `${percent}%`;
  });
}

// -----------------------------------------------------------------------------
// Event binding (idempotent — safe to call after every re-render).
// Works for both `.mini-cart__order` line items (used in the drawer and the
// cart page line list).
// -----------------------------------------------------------------------------
function bindLineItemHandlers() {
  document.querySelectorAll('.mini-cart__order').forEach((product) => {
    if (product.dataset.bound === '1') return;
    product.dataset.bound = '1';

    const key = product.getAttribute('data-key');
    const input = product.querySelector('.quantity-input');
    const plus = product.querySelector('[data-btn-plus]');
    const minus = product.querySelector('[data-btn-minus]');
    const del = product.querySelector('.mini-cart__order-delete');

    if (plus) {
      plus.addEventListener('click', () => {
        const value = (parseInt(input.value, 10) || 0) + 1;
        input.value = value;
        changeItemQuantity(key, value);
      });
    }
    if (minus) {
      minus.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 0;
        if (current > 1) {
          input.value = current - 1;
          changeItemQuantity(key, current - 1);
        } else {
          changeItemQuantity(key, 0);
        }
      });
    }
    if (del) {
      del.addEventListener('click', () => {
        changeItemQuantity(key, 0);
      });
    }
  });
}

// -----------------------------------------------------------------------------
// Cart mutations — always re-render every cart section on the page.
// -----------------------------------------------------------------------------
function changeItemQuantity(key, quantity) {
  showCartOverlay();
  const sections = getCartSectionIdsOnPage();
  const updates = { [key]: quantity };

  fetch(`/cart/update.js?sections=${encodeURIComponent(sections.join(','))}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates }),
  })
    .then((r) => r.json())
    .then((data) => {
      // /cart/update.js with `sections=` returns the updated cart JSON on the
      // top level (total_price, items, …) plus a `sections` map.
      renderCartFromSections(data.sections, data);
    })
    .catch((err) => console.error('changeItemQuantity failed:', err))
    .finally(hideCartOverlay);
}

function processAddToCartProduct(product, form) {
  let add_count = 1;
  if (product.classList.contains('add-with-count')) {
    add_count = parseInt(
      document.querySelector('.counter__value[form="' + form.getAttribute('id') + '"]').value,
      10
    ) || 1;
  }

  // Resolve variant id from the hidden form input; fall back to data-key.
  let variantId = null;
  if (form) {
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) variantId = idInput.value;
  }
  if (!variantId) {
    const rawKey = product.getAttribute('data-key') || '';
    variantId = rawKey.split(':')[0];
  }
  if (!variantId) {
    console.error('processAddToCartProduct: no variant id resolved');
    return;
  }

  showCartOverlay();
  const sections = getCartSectionIdsOnPage();
  fetch(`/cart/add.js?sections=${encodeURIComponent(sections.join(','))}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: add_count }] }),
  })
    .then(async (r) => {
      const addResp = await r.json();
      // /cart/add.js returns the added line item only; fetch full cart for
      // total_price (needed by the progress bar).
      const cartData = await fetch('/cart.js').then((rr) => rr.json());
      renderCartFromSections(addResp.sections, cartData);
    })
    .then(() => showMiniCart())
    .catch((err) => console.error('Add to cart failed:', err))
    .finally(hideCartOverlay);
}

// -----------------------------------------------------------------------------
// Bootstrap
// -----------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  bindLineItemHandlers();

  document.querySelectorAll('button.add-to-mini-cart').forEach((item) => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const form = item.closest('form');
      if (form) form.addEventListener('submit', (ev) => ev.preventDefault());
      const product = e.target.closest('.add-to-mini-cart') || e.target;
      if (item.classList.contains('expiry')) {
        showConfirm('Expiry date: ' + product.getAttribute('data-expiry') + '!').then((ok) => {
          if (ok) processAddToCartProduct(product, form);
        });
      } else {
        processAddToCartProduct(product, form);
      }
    });
  });
});

// Avada upsell offer — re-render after it adds to cart.
let _avadaBound = false;
const _avadaObserver = new MutationObserver(() => {
  if (_avadaBound) return;
  const btn = document.querySelector('.Avada-Offer__ButtonAddToCart');
  if (!btn) return;
  btn.addEventListener('click', () => {
    setTimeout(() => {
      refetchAndRenderCart().then(showMiniCart);
    }, 0);
  });
  _avadaBound = true;
});
_avadaObserver.observe(document.body, { childList: true, subtree: true });